import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Dropdown,
  Field,
  Input,
  OverlayDrawer,
  Option,
  Spinner,
  Switch,
  Text
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { MessageBarType } from '@fluentui/react';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import { connector, Props, useStyles } from './ConsultorioForm.Functions';
import { Consultorio } from '../../../../models/Entities/Turnero';
import './ConsultorioForm.Styles.css';

const ConsultorioFormComponent = ({ isOpen, onClose, defaultSectorId, branchId, consultorioId, isSaving, isSaved, failOnSaving, error, sectores, currentConsultorio, CreateConsultorio, UpdateConsultorio, GetSectores, GetConsultorios }: Props) => {
  const styles = useStyles();

  const [sectorId, setSectorId] = useState<number | undefined>(defaultSectorId);
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const isEditMode = Boolean(consultorioId);
  const sectorLocked = !isEditMode && !!defaultSectorId;
  const activeBranchId = useMemo(() => {
    if (branchId !== undefined && branchId !== null) return branchId;
    const fromSelected = (sectores || []).find((s) => s.id === sectorId);
    const fromDefault = (sectores || []).find((s) => s.id === defaultSectorId);
    return fromSelected?.branchId ?? fromSelected?.branch?.id ?? fromDefault?.branchId ?? fromDefault?.branch?.id;
  }, [branchId, sectores, sectorId, defaultSectorId]);

  const selectedSectorName = useMemo(() => {
    const idToShow = sectorLocked ? (defaultSectorId || sectorId) : sectorId;
    if (!idToShow) return '';
    const found = (sectores || []).find((s) => s.id === idToShow);
    return found?.name || `Sector ${idToShow}`;
  }, [sectores, sectorId, sectorLocked, defaultSectorId]);

  useEffect(() => {
    if (isOpen) {
      GetSectores();
    }
  }, [isOpen, GetSectores]);

  // Sync sectorId when the form opens in create mode (or when defaultSectorId changes)
  useEffect(() => {
    if (isOpen && !isEditMode) {
      setSectorId(defaultSectorId || undefined);
    }
  }, [isOpen, defaultSectorId, isEditMode]);

  useEffect(() => {
    if (isEditMode && !currentConsultorio) {
      GetConsultorios();
    }
  }, [isEditMode, currentConsultorio, GetConsultorios]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isSaved && submitted) {
      resetForm();
      onClose();
    }
  }, [isSaved, submitted, onClose]);

  useEffect(() => {
    if (isEditMode && currentConsultorio) {
      setSectorId(currentConsultorio.sectorId ?? defaultSectorId);
      setNumber(currentConsultorio.number || '');
      setName(currentConsultorio.name || '');
      setIsAvailable(currentConsultorio.isAvailable ?? true);
    }
  }, [isEditMode, currentConsultorio, defaultSectorId]);

  const resetForm = () => {
    setSectorId(defaultSectorId);
    setNumber('');
    setName('');
    setIsAvailable(true);
    setSubmitted(false);
    setValidationErrors([]);
  };

  const validate = () => {
    const errors: string[] = [];
    if (!sectorId && sectorId !== 0) {
      errors.push('Debe seleccionar un sector');
    }
    if (!number.trim()) {
      errors.push('Debe ingresar un número/código');
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload = {
      sectorId: sectorId!,
      number: number.trim(),
      name: name.trim() || null,
      status: isAvailable ? 'Disponible' : 'No disponible',
      isAvailable,
      isLocked: false,
    } as Consultorio;

    if (isEditMode && consultorioId) {
      UpdateConsultorio(consultorioId, { ...payload, id: consultorioId });
    } else {
      CreateConsultorio(payload);
    }
  };

  const sectorOptions = useMemo(() => {
    const list = sectores || [];
    if (!activeBranchId) return list;
    const filtered = list.filter((s) => s.branchId === activeBranchId || s.branch?.id === activeBranchId);
    return filtered.length > 0 ? filtered : list;
  }, [sectores, activeBranchId]);

  return (
    <OverlayDrawer position="end" open={isOpen}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              disabled={isSaving}
            />
          }
        >
          {isEditMode ? 'Editar Consultorio' : 'Crear Consultorio'}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className="consultorio-form-body">
        <div className={styles.container}>
          {validationErrors.length > 0 && (
            <MessageComponent
              message="Verifique la información suministrada"
              type={MessageBarType.error}
              itemList={validationErrors}
            />
          )}
          {failOnSaving === true && (
            <MessageComponent
              message={error?.ErrorMessage || 'Ocurrio un error inesperado'}
              type={MessageBarType.error}
              itemList={error?.Errors}
            />
          )}
          <Field label="Sector" required>
            {sectorLocked ? (
              <Text>{selectedSectorName || 'Sector seleccionado'}</Text>
            ) : (
              <Dropdown
                placeholder="Seleccione un sector"
                value={selectedSectorName}
                selectedOptions={sectorId !== undefined ? [String(sectorId)] : []}
                onOptionSelect={(_, data) => setSectorId(data.optionValue ? Number(data.optionValue) : undefined)}
              >
                {sectorOptions.map((s) => {
                  const label = s.name || `Sector ${s.id}`;
                  return (
                    <Option key={s.id ?? s.name} value={String(s.id)} text={label}>
                      {label}
                    </Option>
                  );
                })}
              </Dropdown>
            )}
          </Field>

          <Field label="Número" required>
            <Input
              value={number}
              onChange={(e) => setNumber(e.currentTarget.value)}
              placeholder="Número o código"
            />
          </Field>

          <Field label="Nombre">
            <Input
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="Nombre (opcional)"
            />
          </Field>

          <Field label="Disponible">
            <Switch checked={isAvailable} onChange={(_, data) => setIsAvailable(data.checked)} label={isAvailable ? 'Sí' : 'No'} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div className={styles.actions}>
          <Button onClick={onClose} disabled={isSaving}>Cancelar</Button>
          <Button appearance="primary" onClick={handleSubmit} disabled={isSaving}>
            {isSaving && <><Spinner size="extra-tiny" /> <Text>Guardando</Text></>}
            {!isSaving && 'Guardar'}
          </Button>
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connector(ConsultorioFormComponent as any);
